// AlarmVerificationForm.js - Engagement-focused alarm verification
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import documentCheckinService from "../../lib/DocumentCheckinService";
import { useAuth } from "../../hooks/useAuth";
import confetti from "canvas-confetti";
import {
  loadFormData,
  saveUploadedFiles,
  loadUploadedFiles,
  clearAllDataForConfig,
} from "../storageUtils";
import track from "../../lib/analytics/events";

const CONFIG_ID = "alarm-verification-nationwide-tx";
const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
];

const AlarmVerificationForm = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const slug = "alarm-monitoring-verification-v1";

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Files now track: { file: File|null, name, size, type, status: 'pending'|'uploading'|'uploaded'|'failed', url: string|null, error: string|null }
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({});

  // Load configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        if (!user) {
          return;
        }
        const token = await getIdToken();
        if (!token) {
          return;
        }
        setLoading(true);
        // Load configuration from server
        const configData = await documentCheckinService.getConfigBySlug(
          slug,
          token
        );

        const parsedConfig = {
          ...configData,
          configuration: JSON.parse(configData.configurationJson),
        };
        setConfig(parsedConfig);

        // NOW load cached form data using the config ID we just got
        const cachedFormData = loadFormData(parsedConfig.id);

        // Initialize form data: use cached values if they exist, otherwise empty string
        const initialFormData = {};
        parsedConfig.configuration.fields.forEach((field) => {
          initialFormData[field.id] = cachedFormData[field.id] || "";
        });
        setFormData(initialFormData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadConfig();
    }
  }, [slug, user, getIdToken]);

  // Initialize and load cached files
  useEffect(() => {
    const initialize = async () => {
      try {
        if (!user) return;

        const token = await getIdToken();
        if (!token) return;

        // Load cached files (these include status and URL if previously uploaded)
        const cachedFiles = loadUploadedFiles(CONFIG_ID);
        if (cachedFiles.monitoringProof && cachedFiles.monitoringProof.length > 0) {
          // Restore files - File objects can't be restored, but uploaded files have URLs
          const restoredFiles = cachedFiles.monitoringProof.map((f) => ({
            file: null, // File object can't be restored from storage
            name: f.name,
            size: f.size,
            type: f.type,
            status: f.status || "pending",
            url: f.url || null,
            error: f.error || null,
          }));
          setFiles(restoredFiles);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [user, getIdToken]);

  // Auto-save uploaded files (only save files that have been uploaded with URLs)
  useEffect(() => {
    // Only persist files that have been successfully uploaded (have a URL)
    // Pending files can't be restored since File objects can't be serialized
    const uploadedFiles = files.filter(
      (f) => f.status === "uploaded" && f.url
    );

    if (uploadedFiles.length > 0) {
      const serializableFiles = uploadedFiles.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        status: f.status,
        url: f.url,
        error: null,
      }));
      saveUploadedFiles(CONFIG_ID, { monitoringProof: serializableFiles });
    } else {
      // Clear storage if no uploaded files
      saveUploadedFiles(CONFIG_ID, { monitoringProof: [] });
    }
  }, [files]);

  // File validation and addition
  const addFiles = (newFiles, source = "picker") => {
    setUploadError(null);

    // Check file count
    if (files.length + newFiles.length > MAX_FILES) {
      track.documentValidationFailed({ reason: "max_files" });
      setUploadError(`You can upload a maximum of ${MAX_FILES} files`);
      return;
    }

    // Validate each file
    const validFiles = [];
    const errors = [];

    newFiles.forEach((file) => {
      // Validate file type
      if (!ACCEPTED_TYPES.includes(file.type)) {
        track.documentValidationFailed({
          reason: "type",
          file_type: file.type,
        });
        errors.push(`${file.name}: Only JPG, PNG, and PDF files are accepted`);
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        track.documentValidationFailed({
          reason: "size",
          file_size_bytes: file.size,
        });
        errors.push(`${file.name}: File is too large (max 10MB)`);
        return;
      }

      // Extra check: make sure file has valid size property
      if (!file.size || typeof file.size !== "number" || isNaN(file.size)) {
        track.documentValidationFailed({ reason: "invalid_size_property" });
        errors.push(`${file.name}: Invalid file`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setUploadError(errors.join(". "));
      return;
    }

    validFiles.forEach((file) => {
      track.documentAdded({
        file_type: file.type,
        file_size_bytes: file.size,
        source,
      });
    });

    // Only add valid files - wrap them with status tracking
    if (validFiles.length > 0) {
      try {
        const wrappedFiles = validFiles.map((file) => ({
          file, // The actual File object
          name: file.name,
          size: file.size,
          type: file.type,
          status: "pending", // pending | uploading | uploaded | failed
          url: null,
          error: null,
        }));
        setFiles((prev) => [...prev, ...wrappedFiles]);
      } catch (e) {
        console.log(e);
      }
    }
  };

  // Event handlers
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles, "picker");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles, "drop");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = (index) => {
    track.documentRemoved();
    setFiles((prev) => prev.filter((_, i) => i !== index));
    // Storage will be updated by the useEffect that watches files
    setUploadError(null);
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      setUploadError(
        "Please share at least one document so we can confirm your alarm monitoring"
      );
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setUploadError(null);

      track.checkinSubmitClicked({ file_count: files.length });

      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // Step 1: Upload files that haven't been uploaded yet
      // Track uploaded URLs locally to avoid async state issues
      const uploadedFiles = [];
      track.documentUploadStarted({
        file_count: files.filter((f) => f.status !== "uploaded").length,
      });

      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];

        // If already uploaded, add to our list and skip
        if (fileData.status === "uploaded" && fileData.url) {
          uploadedFiles.push({
            url: fileData.url,
            name: fileData.name,
            type: fileData.type,
            size: fileData.size,
          });
          continue;
        }

        // Mark file as uploading
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: "uploading", error: null } : f
          )
        );

        try {
          // Upload the file
          const fileUrl = await documentCheckinService.uploadFile(
            fileData.file,
            token
          );

          // Mark file as uploaded and store the URL
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: "uploaded", url: fileUrl } : f
            )
          );
          track.documentUploadSucceeded({ file_type: fileData.type });

          // Track locally for submission
          uploadedFiles.push({
            url: fileUrl,
            name: fileData.name,
            type: fileData.type,
            size: fileData.size,
          });
        } catch (uploadErr) {
          // Mark file as failed
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i
                ? { ...f, status: "failed", error: uploadErr.message }
                : f
            )
          );
          track.documentUploadFailed({
            file_type: fileData.type,
            error: uploadErr?.message,
          });
          setUploadError(`Failed to upload ${fileData.name}. Please try again.`);
          setSubmitting(false);
          return; // Stop on first failure
        }
      }

      // Step 2: All files uploaded, build document URLs for submission
      const documentUrls = uploadedFiles.map((f) => ({
        documentUrl: f.url,
        originalFileName: f.name,
        contentType: f.type,
        fileSizeBytes: f.size,
        userNote: null,
      }));

      // Submit the checkin directly (files already uploaded)
      const result = await documentCheckinService.submitCheckin(
        config.id,
        user.uid,
        [],
        documentUrls,
        token
      );

      track.checkinSubmitSucceeded({ file_count: documentUrls.length });
      clearAllDataForConfig(CONFIG_ID);
      confetti({
        particleCount: 100,
        spread: 80,
        origin: { x: 0.5, y: 0.4 },
        colors: ["#1C57A5", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
      });
      setTimeout(() => {
        navigate(`/rewards`, {
          state: { checkinId: result.id },
        });
      }, 1000);
    } catch (err) {
      console.error("Submission error:", err);
      track.checkinSubmitFailed({ error: err?.message });
      setUploadError(
        err.message ||
          "Unable to submit. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Error state - only for critical initialization errors
  if (error && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Unable to load form
          </h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto stagger-children">
        {/* Header */}
        <Header />

        {/* Main Form Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="p-6 sm:p-8">
              {/* Instructions Section */}
              <InstructionsSection />

              {/* Upload Section */}
              <UploadSection
                isDragging={isDragging}
                uploadError={uploadError}
                onFileSelect={handleFileSelect}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              />

              {/* File List */}
              {files.length > 0 && (
                <FileList files={files} onRemove={removeFile} />
              )}

              {/* Helpful Tips */}
              {/* <HelpfulTips /> */}

              {/* Submit Button */}
              <SubmitButton submitting={submitting} filesCount={files.length} />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ========== SUBCOMPONENTS ==========

const Header = () => (
  <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 mb-6">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0">
        <div className="w-14 h-14 bg-gradient-to-br from-crimsonGlory to-crimsonGlory rounded-xl flex items-center justify-center shadow-lg">
          <svg
            className="w-7 h-7 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
            />
          </svg>
        </div>
      </div>
      <div className="flex-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Confirm Your Alarm Monitoring
        </h1>
        <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
          Upload your document or snap a picture.
        </p>
      </div>
    </div>
  </div>
);

const InstructionsSection = () => (
  <div className="mb-8">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 bg-crimsonGlory rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-white text-lg">✓</span>
      </div>
      <h2 className="text-xl font-semibold text-gray-900">
        What can you share with us?
      </h2>
    </div>

    <p className="text-gray-700 mb-5 leading-relaxed">
      Any of these work great:
    </p>

    <div className="bg-crimsonGlory border-2 rounded-xl p-5">
      <ul className="space-y-2.5">
        <ChecklistItem text="Recent bill or invoice (shows company name, address, monitoring service)" />
        <ChecklistItem text="Screenshot from your alarm app clearly showing your account information." />
        <ChecklistItem text="Screenshot from your alarm company's website  clearly showing your account information." />
        <ChecklistItem text="Photo of your alarm panel or keypad showing the company name" />
        <ChecklistItem text="Photo of equipment like sensors or cameras with branding" />
      </ul>
      <div className="mt-4 pt-4 border-t border-white-200">
        <p className="text-sm text-white italic">
          Don't worry if you can't find everything—share what you have and we'll
          work with you!
        </p>
      </div>
    </div>
  </div>
);

const ChecklistItem = ({ text }) => (
  <li className="flex items-start gap-3 text-crimsonGlory-900">
    <svg
      className="w-5 h-5 text-white mt-0.5 flex-shrink-0"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    <span
      className="leading-relaxed text-white"
      dangerouslySetInnerHTML={{ __html: text }}
    />
  </li>
);

const UploadSection = ({
  isDragging,
  uploadError,
  onFileSelect,
  onDrop,
  onDragOver,
  onDragLeave,
}) => (
  <div className="mb-8">
    <div
      className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 ${
        isDragging
          ? "border-crimsonGlory bg-red-50 scale-[1.02]"
          : uploadError
          ? "border-red-300 bg-red-50"
          : "border-gray-300 hover:border-crimsonGlory/60 bg-gray-50 hover:bg-red-50/30"
      }`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <div className="flex flex-col items-center justify-center">
        <div className="mb-5">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
        </div>

        <label htmlFor="file-upload" className="cursor-pointer group">
          <span className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-crimsonGlory to-crimsonGlory text-white font-semibold rounded-xl hover:from-crimsonGlory/90 hover:to-crimsonGlory/80 transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Add a Photo
          </span>
          <input
            id="file-upload"
            type="file"
            multiple
            accept={ACCEPTED_TYPES.join(",")}
            onChange={onFileSelect}
            className="hidden"
          />
        </label>

        <p className="text-sm text-gray-500 mt-4 font-medium">
          or drag and drop files here
        </p>
        <p className="text-xs text-gray-400 mt-2">
          JPG, PNG, or PDF • Up to {MAX_FILES} files • Max 10MB each
        </p>
      </div>
    </div>

    {uploadError && (
      <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-red-700 font-medium">{uploadError}</p>
        </div>
      </div>
    )}
  </div>
);

const FileList = ({ files, onRemove }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
        Your Documents
      </h3>
      <span className="px-3 py-1 bg-red-100 text-crimsonGlory text-xs font-semibold rounded-full">
        {files.length}/{MAX_FILES}
      </span>
    </div>
    <div className="space-y-3">
      {files.map((file, index) => (
        <FileItem key={index} file={file} onRemove={() => onRemove(index)} />
      ))}
    </div>
  </div>
);

const FileItem = ({ file, onRemove }) => {
  const isPdf = file.type === "application/pdf";
  const status = file.status || "pending";

  // Safely format file size with fallback
  const formatFileSize = (bytes) => {
    if (!bytes || typeof bytes !== "number" || isNaN(bytes)) {
      return "Unknown size";
    }
    return (bytes / 1024 / 1024).toFixed(2) + " MB";
  };

  // Status indicator component
  const StatusIndicator = () => {
    switch (status) {
      case "uploading":
        return (
          <div className="flex-shrink-0 ml-2">
            <svg
              className="animate-spin h-5 w-5 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        );
      case "uploaded":
        return (
          <div className="flex-shrink-0 ml-2">
            <svg
              className="h-5 w-5 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case "failed":
        return (
          <div className="flex-shrink-0 ml-2">
            <svg
              className="h-5 w-5 text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Border color based on status
  const getBorderClass = () => {
    switch (status) {
      case "uploading":
        return "border-blue-300 bg-blue-50/50";
      case "uploaded":
        return "border-green-300 bg-green-50/50";
      case "failed":
        return "border-red-300 bg-red-50/50";
      default:
        return "border-gray-200";
    }
  };

  return (
    <div
      className={`group flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 border rounded-xl hover:shadow-md transition-all ${getBorderClass()}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="flex-shrink-0">
          {isPdf ? (
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-7 h-7 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ) : (
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-7 h-7 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {file.name}
            </p>
            <StatusIndicator />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatFileSize(file.size)}
            {status === "uploading" && (
              <span className="ml-2 text-blue-600">Uploading...</span>
            )}
            {status === "uploaded" && (
              <span className="ml-2 text-green-600">Uploaded</span>
            )}
            {status === "failed" && (
              <span className="ml-2 text-red-600">Failed</span>
            )}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={status === "uploading"}
        className={`flex-shrink-0 ml-4 p-2 rounded-lg transition-all ${
          status === "uploading"
            ? "text-gray-300 cursor-not-allowed"
            : "text-gray-400 hover:text-red-600 hover:bg-red-50 group-hover:scale-110"
        }`}
        aria-label="Remove file"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

const HelpfulTips = () => (
  <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
    <div className="flex gap-4">
      <span className="text-3xl flex-shrink-0">💡</span>
      <div>
        <p className="font-semibold text-blue-900 mb-3">Privacy tips:</p>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>
              Feel free to black out payment details or account numbers
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>Any clear photo works—doesn't need to be perfect!</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>You can upload up to {MAX_FILES} files if needed</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const SubmitButton = ({ submitting, filesCount }) => (
  <div className="pt-2">
    <button
      type="submit"
      disabled={submitting || filesCount === 0}
      className={`w-full py-4 px-6 rounded-xl font-semibold text-white text-lg transition-all transform ${
        submitting || filesCount === 0
          ? "bg-gray-400 cursor-not-allowed"
          : "bg-gradient-to-r from-crimsonGlory to-crimsonGlory hover:from-crimsonGlory/90 hover:to-crimsonGlory/80 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
      }`}
    >
      {submitting ? (
        <span className="flex items-center justify-center gap-3">
          <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Submitting...
        </span>
      ) : (
        "Submit & Confirm"
      )}
    </button>
  </div>
);

export default AlarmVerificationForm;
